// Custom Smart Navigation System
// Purpose: URL-based menu highlighting and custom hover effects
// Dependencies: None
// Settings: custom_enable_smart_navigation

(function () {
  'use strict';

  if (window.CustomSmartNavigation) {
    // Class already defined, exit early
    return;
  }

  window.CustomSmartNavigation = class {
    constructor(element) {
      this.element = element;
      this.urlRules = [];
      this.eventListeners = new Map(); // Store event listeners for cleanup
      this.init();
    }

    init() {
      if (!this.element) return;
      
      this.loadSettings();
      this.setupUrlBasedSelection();
      this.setupHoverEffects();
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) console.log('Custom Smart Navigation: Initialized with', this.urlRules.length, 'URL rules for element', this.element);
    }
    
    loadSettings() {
      // Extract URL-based selection rules from JSON configuration
      const configScripts = document.querySelectorAll('script[data-smart-nav-config]');
      
      const seenRules = new Set();
      
      configScripts.forEach(script => {
        try {
          const config = JSON.parse(script.textContent);
          
          if (config.urlRules && Array.isArray(config.urlRules)) {
            config.urlRules.forEach(rule => {
              if (rule.keyword && rule.targetMenu) {
                const ruleKey = `${rule.keyword.toLowerCase().trim()}:${rule.targetMenu.trim()}`;
                
                // Only add if we haven't seen this rule combination before
                if (!seenRules.has(ruleKey)) {
                  seenRules.add(ruleKey);
                  this.urlRules.push({
                    keyword: rule.keyword.toLowerCase().trim(),
                    targetMenu: rule.targetMenu.trim(),
                    blockId: script.dataset.blockId
                  });
                }
              }
            });
          }
        } catch (e) {
          console.warn('Custom Smart Navigation: Failed to parse config', e);
        }
      });
      
      // Fallback: Try to extract from inline JSON if available
      if (this.urlRules.length === 0) {
        this.loadSettingsFromGlobal();
      }
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) console.log('Custom Smart Navigation: Loaded', this.urlRules.length, 'URL rules');
    }
    
    loadSettingsFromGlobal() {
      // Fallback approach to extract settings from theme JSON
      try {
        if (window.themeSettings?.headerMenuRules) {
          this.urlRules = window.themeSettings.headerMenuRules;
        }
      } catch (e) {
        console.warn('Custom Smart Navigation: Could not load theme settings');
      }
    }
    
    setupUrlBasedSelection() {
      if (this.urlRules.length === 0) return;
      
      const currentUrl = window.location.pathname.toLowerCase();
      let matchedRule = null;
      
      // Check if we're on a product or collection page
      if (currentUrl.includes('/products/') || currentUrl.includes('/collections/')) {
        // Find the first matching rule
        matchedRule = this.urlRules.find(rule => 
          (currentUrl.includes('/products/') && currentUrl.includes(rule.keyword)) ||
          (currentUrl.includes('/collections/') && currentUrl.includes(rule.keyword))
        );
      }
      
      if (matchedRule) {
        this.applyCustomSelection(matchedRule.targetMenu, matchedRule.block);
      }
    }
    
    applyCustomSelection(targetMenuTitle, block = null) {
      // Find all menu items (search globally if no specific block)
      const selector = block ? 
        `${block.tagName}[data-block-type="_header-menu"] .menu-list__link, ${block.tagName}[data-block-type="_header-menu"] .menu-list__item a` :
        '.menu-list__link, .menu-list__item a';
      
      const menuItems = document.querySelectorAll(selector);
      
      // Get styling configuration from the hook data
      const hookData = document.querySelector('[data-smart-nav="enabled"]');
      const hoverStyle = hookData?.dataset.hoverStyle;
      const selectedStyle = hookData?.dataset.selectedStyle;
      const hoverColor = hookData?.dataset.hoverColor;
      const selectedColor = hookData?.dataset.selectedColor;
      const selectedThickness = hookData?.dataset.selectedThickness;
      
      menuItems.forEach(item => {
        const title = item.textContent?.trim();
        
        // Apply data attributes for styling (moved from core file)
        if (hoverStyle) item.setAttribute('data-hover-style', hoverStyle);
        if (selectedStyle) item.setAttribute('data-selected-style', selectedStyle);
        
        // Set CSS custom properties for styling
        if (hoverColor) item.style.setProperty('--hover-color', hoverColor);
        if (selectedColor) item.style.setProperty('--selected-color', selectedColor);
        if (selectedThickness) item.style.setProperty('--selected-thickness', selectedThickness + 'px');
        
        if (title === targetMenuTitle) {
          // Apply selected state
          item.setAttribute('aria-current', 'page');
          item.classList.add('menu-list__link--active');
          
          // Apply selected style based on data attribute
          if (selectedStyle) {
            item.classList.add(`menu-selected--${selectedStyle}`);
          }
        } else {
          // Remove selected state from other items
          item.removeAttribute('aria-current');
          item.classList.remove('menu-list__link--active');
          
          // Remove all selected style classes
          const classList = Array.from(item.classList);
          classList.forEach(className => {
            if (className.startsWith('menu-selected--')) {
              item.classList.remove(className);
            }
          });
        }
      });
    }
    
    setupHoverEffects() {
      // Apply styling configuration to all menu items first
      this.applyGlobalStyling();
      
      // Enhanced hover effects are primarily handled via CSS
      // This adds JavaScript support for complex animations
      const menuItems = document.querySelectorAll('.menu-list__link[data-hover-style]');
      
      menuItems.forEach(item => {
        const hoverStyle = item.dataset.hoverStyle;
        const hoverColor = item.style.getPropertyValue('--hover-color');
        const hoverDuration = item.style.getPropertyValue('--hover-duration') || '200ms';
        
        // Add enhanced JavaScript behaviors for certain hover styles
        switch (hoverStyle) {
          case 'glow':
            this.setupGlowHover(item, hoverColor);
            break;
          case 'scale':
            this.setupScaleHover(item);
            break;
        }
      });
    }
    
    applyGlobalStyling() {
      // Get styling configuration from the hook data  
      const hookData = document.querySelector('[data-smart-nav="enabled"]');
      if (!hookData) return;
      
      const hoverStyle = hookData.dataset.hoverStyle;
      const selectedStyle = hookData.dataset.selectedStyle;
      const hoverColor = hookData.dataset.hoverColor;
      const selectedColor = hookData.dataset.selectedColor;
      const selectedThickness = hookData.dataset.selectedThickness;
      
      // Apply to all menu items
      const allMenuItems = document.querySelectorAll('.menu-list__link, .menu-list__item a');
      allMenuItems.forEach(item => {
        // Apply data attributes for styling (moved from core file)
        if (hoverStyle) item.setAttribute('data-hover-style', hoverStyle);
        if (selectedStyle) item.setAttribute('data-selected-style', selectedStyle);
        
        // Set CSS custom properties for styling
        if (hoverColor) item.style.setProperty('--hover-color', hoverColor);
        if (selectedColor) item.style.setProperty('--selected-color', selectedColor);
        if (selectedThickness) item.style.setProperty('--selected-thickness', selectedThickness + 'px');
      });
    }
    
    setupGlowHover(item, color) {
      const mouseenterHandler = () => {
        item.style.transition = `text-shadow var(--hover-duration, 200ms) var(--animation-easing)`;
        item.style.textShadow = `0 0 8px ${color || 'currentColor'}`;
      };
      
      const mouseleaveHandler = () => {
        item.style.textShadow = '';
      };
      
      item.addEventListener('mouseenter', mouseenterHandler);
      item.addEventListener('mouseleave', mouseleaveHandler);
      
      // Store handlers for cleanup
      this.eventListeners.set(item, {
        mouseenter: mouseenterHandler,
        mouseleave: mouseleaveHandler
      });
    }
    
    setupScaleHover(item) {
      const mouseenterHandler = () => {
        item.style.transition = `transform var(--hover-duration, 200ms) var(--animation-easing)`;
        item.style.transform = 'scale(1.05)';
      };
      
      const mouseleaveHandler = () => {
        item.style.transform = '';
      };
      
      item.addEventListener('mouseenter', mouseenterHandler);
      item.addEventListener('mouseleave', mouseleaveHandler);
      
      // Store handlers for cleanup (if item already has listeners, merge them)
      const existingListeners = this.eventListeners.get(item) || {};
      this.eventListeners.set(item, {
        ...existingListeners,
        'mouseenter-scale': mouseenterHandler,
        'mouseleave-scale': mouseleaveHandler
      });
    }

    destroy() {
      // Remove all stored event listeners
      this.eventListeners.forEach((handlers, element) => {
        Object.entries(handlers).forEach(([eventType, handler]) => {
          // Handle compound event types (e.g., 'mouseenter-scale')
          const baseEventType = eventType.split('-')[0];
          element.removeEventListener(baseEventType, handler);
        });
        
        // Reset inline styles that were applied
        element.style.removeProperty('text-shadow');
        element.style.removeProperty('transform');
        element.style.removeProperty('transition');
        element.style.removeProperty('--hover-color');
        element.style.removeProperty('--selected-color');
        element.style.removeProperty('--selected-thickness');
        
        // Remove applied data attributes
        element.removeAttribute('data-hover-style');
        element.removeAttribute('data-selected-style');
        element.removeAttribute('aria-current');
        
        // Remove CSS classes
        element.classList.remove('menu-list__link--active');
        const classList = Array.from(element.classList);
        classList.forEach(className => {
          if (className.startsWith('menu-selected--')) {
            element.classList.remove(className);
          }
        });
      });
      
      // Clear stored references
      this.eventListeners.clear();
      this.urlRules = [];
      this.element = null;
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) console.log('Custom Smart Navigation: Destroyed and cleaned up');
    }
  };

  const SELECTOR = '[data-smart-nav="enabled"]';
  const INIT_ATTR = 'data-custom-smart-nav-init';

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.hasAttribute(INIT_ATTR)) {
        const instance = new window.CustomSmartNavigation(el);
        el._customSmartNavigationInstance = instance; // Store instance for cleanup
        el.setAttribute(INIT_ATTR, 'true');
      }
    });
  }

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', () => initAll());

  // Theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    document.addEventListener('shopify:section:unload', (e) => {
      // Proper cleanup with destroy() method
      e.target.querySelectorAll(`${SELECTOR}[${INIT_ATTR}]`).forEach(el => {
        if (el._customSmartNavigationInstance) {
          el._customSmartNavigationInstance.destroy();
          delete el._customSmartNavigationInstance;
        }
        el.removeAttribute(INIT_ATTR);
      });
    });
  }
})();